import React from 'react';
import {
  Modal,
  Card,
  Statistic,
  Row,
  Col,
  Typography,
  Progress,
  Table,
  Tag,
  Space,
  Button,
  Alert,
  Descriptions,
  List,
  Collapse,
  Empty,
  Spin,
  message,
  theme
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
  ReloadOutlined,
  BarChartOutlined,
  TeamOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { formatDate } from '../../utils';
import {
  ValidationReportModalProps,
  ValidationResult
} from '../../../../types/validation';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const RapportValidation: React.FC<ValidationReportModalProps> = ({
  visible,
  report,
  onCancel,
  onRefresh,
  loading = false
}) => {
  const { token } = theme.useToken();

  const exportReport = async () => {
    try {
      if (!report) return;
      const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: 'application/json;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url; link.download = `rapport-validation-paie-${report.period_id}.json`;
      document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); URL.revokeObjectURL(url); }
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      message.error('Erreur lors de l\'export du rapport');
    }
  };

  const getValidationStatusIcon = (result: ValidationResult) => {
    if (result.is_valid && result.errors.length === 0) {
      return <CheckCircleOutlined style={{ color: token.colorSuccess }} />;
    }
    if (result.errors.length > 0) {
      return <CloseCircleOutlined style={{ color: token.colorError }} />;
    }
    if (result.warnings.length > 0) {
      return <ExclamationCircleOutlined style={{ color: token.colorWarning }} />;
    }
    return <CheckCircleOutlined style={{ color: token.colorSuccess }} />;
  };

  const getValidationRate = () => {
    if (!report?.summary) return 0;
    const { summary } = report;
    // Le backend retourne validated_calculations au lieu de valid_calculations
    const validatedCount = Number((summary as any).validated_calculations ?? summary.valid_calculations ?? 0);
    const total = Number(summary.total_employees ?? summary.total_calculations ?? 0);
    return total > 0
      ? Math.round((validatedCount / total) * 100)
      : 0;
  };

  const renderSummarySection = () => {
    if (!report?.summary) return null;

    const { summary } = report;
    const validationRate = getValidationRate();
    
    // Adapter aux champs retournés par le backend
    const validatedCount = Number((summary as any).validated_calculations ?? summary.valid_calculations ?? 0);
    const errorsCount = Number((summary as any).validation_errors ?? report.calculations_with_errors?.length ?? (summary as any).error_calculations ?? summary.calculations_with_errors ?? 0);
    const warningsCount = report.calculations_with_warnings?.length ?? summary.calculations_with_warnings ?? 0;

    return (
      <div>
        {/* Statistiques principales */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Calculs"
                value={summary.total_calculations || 0}
                prefix={<FileTextOutlined style={{ color: token.colorInfo }} />}
                valueStyle={{ color: token.colorInfo }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Validés"
                value={validatedCount}
                prefix={<CheckCircleOutlined style={{ color: token.colorSuccess }} />}
                valueStyle={{ color: token.colorSuccess }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Avec Erreurs"
                value={errorsCount}
                prefix={<CloseCircleOutlined style={{ color: token.colorError }} />}
                valueStyle={{ color: token.colorError }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Avec Avertissements"
                value={warningsCount}
                prefix={<WarningOutlined style={{ color: token.colorWarning }} />}
                valueStyle={{ color: token.colorWarning }}
              />
            </Card>
          </Col>
        </Row>

        {/* Taux de validation */}
        <Card title="Taux de Validation" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={12}>
              <Progress
                type="circle"
                percent={validationRate}
                format={percent => `${percent}%`}
                status={validationRate >= 95 ? 'success' : validationRate >= 80 ? 'active' : 'exception'}
                strokeColor={{
                  '0%': validationRate >= 95 ? token.colorSuccess : validationRate >= 80 ? token.colorInfo : token.colorError,
                  '100%': validationRate >= 95 ? '#73d13d' : validationRate >= 80 ? '#40a9ff' : '#ff7875',
                }}
                size={120}
              />
            </Col>
            <Col span={12}>
              <div>
                <Title level={4}>Évaluation</Title>
                {validationRate >= 95 && (
                  <Alert
                    message="Excellent"
                    description="Taux de validation très satisfaisant"
                    type="success"
                    showIcon
                  />
                )}
                {validationRate >= 80 && validationRate < 95 && (
                  <Alert
                    message="Bon"
                    description="Taux de validation acceptable"
                    type="info"
                    showIcon
                  />
                )}
                {validationRate < 80 && (
                  <Alert
                    message="À améliorer"
                    description="Plusieurs calculs nécessitent une attention"
                    type="warning"
                    showIcon
                  />
                )}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Répartition des erreurs */}
        {errorsCount > 0 && (
          <Card title="Répartition des Erreurs" style={{ marginBottom: 24 }}>
            <Progress
              percent={100}
              success={{ percent: validationRate }}
              strokeColor="#ff4d4f"
              format={() => 
                `${errorsCount} erreur(s) sur ${summary.total_calculations || 0}`
              }
            />
          </Card>
        )}
      </div>
    );
  };

  const renderDetailsSection = () => {
    if (!report || !report.results) return null;

    const columns = [
      {
        title: 'Employé',
        dataIndex: 'employee_name',
        key: 'employee_name',
        sorter: (a: ValidationResult, b: ValidationResult) => 
          a.employee_name.localeCompare(b.employee_name),
      },
      {
        title: 'Statut',
        key: 'status',
        render: (_: any, record: ValidationResult) => (
          <Space>
            {getValidationStatusIcon(record)}
            {record.is_valid ? (
              <Tag color="success">{record.is_validated ? 'Validé' : 'Cohérent'}</Tag>
            ) : record.errors.length > 0 ? (
              <Tag color="error">Erreurs</Tag>
            ) : (
              <Tag color="warning">Avertissements</Tag>
            )}
          </Space>
        ),
        filters: [
          { text: 'Cohérent', value: 'valid' },
          { text: 'Erreurs', value: 'errors' },
          { text: 'Avertissements', value: 'warnings' },
        ],
        onFilter: (value: any, record: ValidationResult) => {
          if (value === 'valid') return record.is_valid && record.errors.length === 0;
          if (value === 'errors') return record.errors.length > 0;
          if (value === 'warnings') return record.warnings.length > 0;
          return true;
        },
      },
      {
        title: 'Problèmes',
        key: 'issues',
        render: (_: any, record: ValidationResult) => (
          <Space>
            {record.errors.length > 0 && (
              <Tag color="error">{record.errors.length} erreur(s)</Tag>
            )}
            {record.warnings.length > 0 && (
              <Tag color="warning">{record.warnings.length} avertissement(s)</Tag>
            )}
            {record.errors.length === 0 && record.warnings.length === 0 && (
              <Text type="secondary">Aucun</Text>
            )}
          </Space>
        ),
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={report.results}
        rowKey="calculation_id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} sur ${total} calculs`,
        }}
        expandable={{
          expandedRowRender: (record: ValidationResult) => (
            <div style={{ margin: 0 }}>
              {record.errors.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Title level={5} style={{ color: token.colorError }}>Erreurs:</Title>
                  <List
                    size="small"
                    dataSource={record.errors}
                    renderItem={(error, index) => (
                      <List.Item key={index}>
                        <Alert
                          message={error.message}
                          description={`Champ: ${error.field} | Valeur: ${error.current_value}`}
                          type="error"
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )}
              {record.warnings.length > 0 && (
                <div>
                  <Title level={5} style={{ color: token.colorWarning }}>Avertissements:</Title>
                  <List
                    size="small"
                    dataSource={record.warnings}
                    renderItem={(warning, index) => (
                      <List.Item key={index}>
                        <Alert
                          message={warning.message}
                          description={`Champ: ${warning.field} | Valeur: ${warning.current_value}`}
                          type="warning"
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </div>
          ),
          rowExpandable: (record: ValidationResult) => 
            record.errors.length > 0 || record.warnings.length > 0,
        }}
      />
    );
  };

  const renderConstraintsSection = () => {
    if (!report || !report.constraints_applied) return null;

    return (
      <List
        header={<Title level={4}>Contraintes Appliquées</Title>}
        dataSource={report.constraints_applied}
        renderItem={(constraint, index) => (
          <List.Item key={index}>
            <Card size="small" style={{ width: '100%' }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Text strong>Champ:</Text> {constraint.field}
                </Col>
                <Col span={4}>
                  <Text strong>Opérateur:</Text> {constraint.operator}
                </Col>
                <Col span={6}>
                  <Text strong>Valeur:</Text> {constraint.value}
                </Col>
                <Col span={8}>
                  <Text strong>Message:</Text> {constraint.message}
                </Col>
              </Row>
            </Card>
          </List.Item>
        )}
      />
    );
  };

  if (!visible) return null;

  return (
    <Modal
      title={
        <Space>
          <BarChartOutlined />
          Rapport de Validation
          {report && <Text type="secondary">- {report.period_name}</Text>}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={
        <Space>
          <Button onClick={onCancel}>Fermer</Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            loading={loading}
          >
            Actualiser
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportReport}
            disabled={!report || loading}
          >
            Exporter JSON
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        {report ? (
          <div>
            {/* En-tête du rapport */}
            <Descriptions 
              title="Informations du Rapport" 
              bordered 
              size="small" 
              style={{ marginBottom: 24 }}
            >
              <Descriptions.Item label="Période" span={2}>
                {report.period_name}
              </Descriptions.Item>
              <Descriptions.Item label="Généré le">
                {formatDate(report.generated_at)}
              </Descriptions.Item>
            </Descriptions>

            {/* Contenu principal avec onglets */}
            <Collapse defaultActiveKey={['summary']} style={{ marginBottom: 16 }}>
              <Panel 
                header={
                  <Space>
                    <BarChartOutlined />
                    <span>Résumé Exécutif</span>
                  </Space>
                } 
                key="summary"
              >
                {renderSummarySection()}
              </Panel>

              <Panel 
                header={
                  <Space>
                    <TeamOutlined />
                    <span>Détails par Employé</span>
                  </Space>
                } 
                key="details"
              >
                {renderDetailsSection()}
              </Panel>

              <Panel 
                header={
                  <Space>
                    <FileTextOutlined />
                    <span>Contraintes Appliquées</span>
                  </Space>
                } 
                key="constraints"
              >
                {renderConstraintsSection()}
              </Panel>
            </Collapse>
          </div>
        ) : (
          <Empty
            description="Aucun rapport de validation disponible"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Spin>
    </Modal>
  );
};

export default RapportValidation;
